import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../api";
import { Award, Calendar, MapPin, Users, Heart, Star, MessageCircle, Repeat, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../auth";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { useConfirm } from "../components/ConfirmProvider";
import { redirectToCheckout } from "../lib/checkout";
import { LEGAL_VERSION } from "../legal/texts";
import PublicCalendar from "../components/PublicCalendar";
import ReportDialog from "../components/ReportDialog";

const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });

export default function MnsDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [mns, setMns] = useState(null);
  const [avails, setAvails] = useState([]);
  const [pools, setPools] = useState({});
  const [open, setOpen] = useState(false);
  const [recurOpen, setRecurOpen] = useState(false);
  const [pick, setPick] = useState(null);
  const [method, setMethod] = useState("online");
  const [ageGroup, setAgeGroup] = useState("adult");
  const [level, setLevel] = useState("debutant");
  const [busy, setBusy] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [reviews, setReviews] = useState([]);
  const confirm = useConfirm();

  const reloadFav = () => {
    if (!user || user.role !== "client") return;
    api.get("/favorites").then((r) => setIsFav(r.data.some((f) => f.target_type === "mns" && f.target_id === id))).catch(() => {});
  };

  useEffect(() => {
    api.get(`/mns/${id}`).then((r) => setMns(r.data));
    const from = new Date().toISOString();
    const to = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString();
    api.get("/availabilities", { params: { mns_id: id, from_dt: from, to_dt: to } }).then((r) => setAvails(r.data));
    api.get("/pools").then((r) => {
      const m = {}; r.data.forEach((p) => { m[p.pool_id] = p; });
      setPools(m);
    });
    api.get(`/reviews/mns/${id}`).then((r) => setReviews(r.data)).catch(() => {});
    reloadFav();
    // eslint-disable-next-line
  }, [id, user?.user_id]);

  const toggleFav = async () => {
    if (!user) { toast.error("Connectez-vous pour ajouter aux favoris"); nav("/login"); return; }
    if (isFav) {
      await api.delete("/favorites", { params: { target_type: "mns", target_id: id } });
      toast.success("Retiré des favoris");
    } else {
      await api.post("/favorites", { target_type: "mns", target_id: id });
      toast.success("Ajouté aux favoris");
    }
    reloadFav();
  };

  const book = async () => {
    if (!user) { toast.error("Veuillez vous connecter"); nav("/login"); return; }
    if (user.role !== "client") { toast.error("Seuls les clients peuvent réserver"); return; }
    setBusy(true);
    try {
      // Both online and cash slots go through the cart so the client can group
      // multiple reservations and continue browsing afterwards.
      await api.post("/cart/items", {
        avail_id: pick.avail_id, age_group: ageGroup, level,
        payment_method: method,
      });
      toast.success(
        method === "online"
          ? "Ajouté au panier — vous pourrez tout payer en une fois"
          : "Ajouté au panier — paiement sur place",
        {
          action: { label: "Voir le panier", onClick: () => nav("/cart") },
        },
      );
      window.dispatchEvent(new Event("cart:updated"));
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  if (!mns) return <div className="p-12 text-center text-slate-500">Chargement...</div>;
  const p = mns.profile || {};
  const u = mns.user;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
      <div className="grid lg:grid-cols-[320px_1fr] gap-8">
        <div className="card-soft p-6 h-fit" data-testid="mns-profile-card">
          <img src={u.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.first_name + ' ' + p.last_name)}&background=0369A1&color=fff&size=200`} alt="" className="w-32 h-32 rounded-full object-cover mx-auto ring-4 ring-sky-100" />
          <h1 className="text-2xl font-bold text-center mt-4 text-slate-900" style={{ fontFamily: "Outfit" }}>{p.first_name} {p.last_name}</h1>
          <p className="text-center text-sky-700 text-sm mt-1 flex items-center justify-center gap-1"><Award className="w-4 h-4" />MNS diplômé</p>
          {mns.verified && (
            <div className="mt-2 flex justify-center" data-testid="verified-badge">
              <span className="inline-flex items-center gap-1 text-xs bg-gradient-to-r from-emerald-500 to-sky-600 text-white px-3 py-1 rounded-full font-semibold shadow-sm" title="SIRET, assurance professionnelle et diplôme vérifiés par l'équipe MySwimmingLessons">
                <ShieldCheck className="w-3.5 h-3.5" /> Profil 100% vérifié
              </span>
            </div>
          )}
          {mns.avg_rating ? (
            <div className="flex items-center justify-center gap-1 mt-2 text-sm">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <span className="font-semibold">{mns.avg_rating}</span>
              <span className="text-slate-500">({mns.reviews_count} avis)</span>
            </div>
          ) : (
            <div className="text-center text-xs text-slate-400 mt-2">Pas encore d'avis</div>
          )}
          {mns.boost && <div className="mt-2 text-center"><span className="inline-block text-xs bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-semibold">⭐ Profil boosté</span></div>}
          {user?.role === "client" && (
            <>
              <Button onClick={toggleFav} variant="outline" className={`w-full rounded-full mt-3 ${isFav ? "border-rose-400 text-rose-600" : ""}`} data-testid="fav-mns-btn">
                <Heart className={`w-4 h-4 mr-1 ${isFav ? "fill-rose-500 text-rose-500" : ""}`} />
                {isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
              </Button>
              <Button onClick={() => nav(`/messages/${id}`)} className="w-full rounded-full mt-2 bg-sky-700 hover:bg-sky-600" data-testid="message-mns-btn">
                <MessageCircle className="w-4 h-4 mr-1" />Envoyer un message
              </Button>
              <Button onClick={() => setRecurOpen(true)} variant="outline" className="w-full rounded-full mt-2" data-testid="recur-mns-btn">
                <Repeat className="w-4 h-4 mr-1" />Réserver une série de cours
              </Button>
            </>
          )}
          {user && user.user_id !== id && (
            <div className="mt-2">
              <ReportDialog
                targetType="user"
                targetId={id}
                targetLabel={`${p.first_name} ${p.last_name}`}
                onBlocked={() => nav("/map")}
              />
            </div>
          )}
          <p className="text-slate-600 text-sm mt-4">{p.bio}</p>
          <div className="mt-4 space-y-2 text-sm">
            {/* Iter39: show price range from slots when prices differ, single price otherwise. */}
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Tarif horaire</span>
              <span className="font-semibold text-sky-700" data-testid="mns-price-range">
                {(() => {
                  const pr = mns.price_range;
                  if (!pr || pr.min == null) return <span className="text-slate-400">À définir</span>;
                  if (pr.single) return `${pr.min}€/h`;
                  return `entre ${pr.min} et ${pr.max}€/h`;
                })()}
              </span>
            </div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Niveaux mixtes</span><span>{p.accepts_mixed_levels ? "Oui" : "Non"}</span></div>
            {p.at_home_available && (
              <div className="flex items-center justify-between"><span className="text-slate-500">Cours à domicile</span><span>Oui</span></div>
            )}
          </div>
          {p.diplomas?.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Diplômes</div>
              {p.diplomas.map((d, i) => (
                <div key={`${d.name}-${d.valid_until || i}`} className="text-sm text-slate-700 mb-1">
                  • {d.name} <span className="text-xs text-slate-500">(valide jusqu'au {d.valid_until})</span>
                </div>
              ))}
            </div>
          )}
          {(p.siret || p.vat_subject !== undefined) && (
            <div className="mt-4 border-t pt-4 text-xs text-slate-600 space-y-0.5" data-testid="mns-legal-info">
              <div className="uppercase tracking-widest text-slate-500 text-[10px] mb-1">Informations légales</div>
              {p.siret && <div>SIRET : <span className="font-mono">{p.siret}</span></div>}
              <div>TVA : {p.vat_subject ? `Soumis (${p.vat_rate || 20}%)` : "Non soumis (franchise en base)"}</div>
            </div>
          )}
        </div>

        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}><Calendar className="inline w-5 h-5 mr-1 text-sky-700" />Créneaux disponibles</h2>
          {avails.length === 0 ? (
            <div className="card-soft p-8 text-center text-slate-500">
              <Calendar className="w-10 h-10 mx-auto text-sky-300 mb-2" />
              <p>Aucun créneau publié pour le moment.</p>
              <p className="text-xs text-slate-400 mt-1">Revenez plus tard ou contactez ce MNS via la messagerie.</p>
            </div>
          ) : (
            <PublicCalendar
              availabilities={avails}
              pools={pools}
              onPick={(a) => { setPick(a); setOpen(true); }}
            />
          )}
        </div>
      </div>

      <div className="mt-10">
        <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
          Avis des clients ({reviews.length})
        </h2>
        {reviews.length === 0 ? (
          <div className="card-soft p-6 text-center text-slate-500">Aucun avis pour le moment.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="reviews-list">
            {reviews.map((r) => (
              <div key={r.review_id} className="card-soft p-4">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-900">{r.client_name}</div>
                  <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</div>
                </div>
                {r.comment && <p className="text-sm text-slate-600 mt-2">{r.comment}</p>}
                <div className="text-xs text-slate-400 mt-2">{new Date(r.created_at).toLocaleDateString("fr-FR")}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Réserver ce créneau</DialogTitle></DialogHeader>
          {pick && (
            <div className="space-y-4">
              <div className="text-slate-700">Créneau : <strong>{fmt(pick.start_dt)}</strong></div>
              <div className="bg-sky-50 p-3 rounded-xl text-sm">
                {/* Iter39 fix: use the slot's own price when set (per-slot override).
                    Fallback to mns hourly_rate * duration only if slot has no explicit price. */}
                {(() => {
                  const hours = Math.max(0.5, (new Date(pick.end_dt) - new Date(pick.start_dt)) / 3600000);
                  const base = pick.price != null && pick.price !== ""
                    ? Number(pick.price)
                    : Number(p.hourly_rate || 0) * hours;
                  const hourly = (base / hours);
                  const total = base * 1.1;
                  return (
                    <>
                      Tarif du cours : <strong data-testid="picker-price-base">{base.toFixed(2)}€</strong>
                      {" "}({hourly.toFixed(2)}€/h) + 10% frais de service<br />
                      Total à payer : <strong data-testid="picker-price-total">{total.toFixed(2)}€</strong>
                    </>
                  );
                })()}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Âge</Label>
                  <RadioGroup value={ageGroup} onValueChange={setAgeGroup} className="grid grid-cols-2 gap-2 mt-1">
                    <label className={`border rounded-lg p-2 cursor-pointer flex items-center gap-1 text-sm ${ageGroup === "child" ? "border-sky-600 bg-sky-50" : ""}`}>
                      <RadioGroupItem value="child" data-testid="age-child" /><span>Enfant</span>
                    </label>
                    <label className={`border rounded-lg p-2 cursor-pointer flex items-center gap-1 text-sm ${ageGroup === "adult" ? "border-sky-600 bg-sky-50" : ""}`}>
                      <RadioGroupItem value="adult" data-testid="age-adult" /><span>Adulte</span>
                    </label>
                  </RadioGroup>
                </div>
                <div>
                  <Label>Niveau</Label>
                  <select className="w-full border rounded-lg p-2 mt-1 text-sm" value={level} onChange={(e) => setLevel(e.target.value)} data-testid="level-select">
                    <option value="debutant">Débutant</option>
                    <option value="intermediaire">Intermédiaire</option>
                    <option value="perfectionnement">Perfectionnement</option>
                  </select>
                </div>
              </div>
              {!p.accepts_mixed_levels && <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded-lg">⚠ Ce MNS n'accepte pas les niveaux mixtes : si quelqu'un a déjà réservé ce créneau, vos critères doivent correspondre.</div>}
              <div>
                <Label>Mode de paiement</Label>
                <RadioGroup value={method} onValueChange={setMethod} className="grid grid-cols-2 gap-2 mt-2">
                  <label className={`border rounded-xl p-3 cursor-pointer flex items-center gap-2 ${method === "online" ? "border-sky-600 bg-sky-50" : ""}`}>
                    <RadioGroupItem value="online" data-testid="pay-online" /><span>En ligne (Stripe)</span>
                  </label>
                  <label className={`border rounded-xl p-3 cursor-pointer flex items-center gap-2 ${method === "cash" ? "border-sky-600 bg-sky-50" : ""}`}>
                    <RadioGroupItem value="cash" data-testid="pay-cash" /><span>Espèces sur place</span>
                  </label>
                </RadioGroup>
              </div>
              {p.pool_entry_fee_at_reception && <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded-lg">⚠ L'entrée piscine est à régler à l'accueil de l'établissement.</div>}
              <DialogFooter>
                <Button onClick={book} disabled={busy} className="rounded-full bg-sky-700 hover:bg-sky-600 w-full" data-testid="confirm-booking-btn">
                  {busy ? "Traitement..." : (method === "online" ? "Ajouter au panier" : "Confirmer la réservation")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <RecurringDialog open={recurOpen} onOpenChange={setRecurOpen} mnsId={id} hourlyRate={p.hourly_rate} pools={pools} attachedPoolIds={p.attached_pool_ids || []} />

    </div>
  );
}

function RecurringDialog({ open, onOpenChange, mnsId, hourlyRate, pools, attachedPoolIds }) {
  const nav = useNavigate();
  const confirm = useConfirm();
  const [form, setForm] = useState({ pool_id: "", weekday: 1, hour: 18, minute: 0, duration_minutes: 60, weeks: 4, payment_method: "online" });
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const WD = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

  React.useEffect(() => {
    if (open && attachedPoolIds.length && !form.pool_id) setForm((f) => ({ ...f, pool_id: attachedPoolIds[0] }));
    /* eslint-disable-next-line */
  }, [open]);

  const runCheck = async () => {
    try {
      const { data } = await api.get("/availabilities/recurring-check", { params: {
        mns_id: mnsId, weekday: form.weekday, hour: form.hour,
        minute: form.minute, duration_minutes: form.duration_minutes, weeks: form.weeks,
      }});
      setCheck(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  React.useEffect(() => { if (open) runCheck(); /* eslint-disable-next-line */ }, [open, form.weekday, form.hour, form.weeks, form.duration_minutes]);

  const submit = async () => {
    if (!form.pool_id) return toast.error("Choisissez un établissement");
    if (!check || check.available_count === 0) return toast.error("Aucun créneau disponible sur la période");
    if (form.payment_method === "online" && !cgvAccepted) return toast.error("Acceptez les CGV pour le paiement en ligne");
    const totalEstimated = (check.available_count * hourlyRate * (form.duration_minutes/60) * 1.10).toFixed(2);
    const ok = await confirm({
      title: "Confirmer la réservation récurrente",
      body: form.payment_method === "online"
        ? `Vous allez réserver ${check.available_count} cours et payer la totalité en une fois (${totalEstimated}€).`
        : `Vous allez réserver ${check.available_count} cours (sur ${form.weeks} demandés) chaque ${WD[form.weekday]} à ${String(form.hour).padStart(2,"0")}:${String(form.minute).padStart(2,"0")}. Vous réglerez ${totalEstimated}€ en espèces séance par séance.`,
      confirmLabel: form.payment_method === "online" ? "Payer et réserver" : "Tout réserver",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/recurring", { mns_id: mnsId, ...form });
      if (form.payment_method === "online") {
        const ids = (data.created || []).map((b) => b.booking_id);
        if (ids.length === 0) { toast.error("Aucun créneau réservé"); return; }
        const { data: ck } = await api.post("/bookings/recurring/checkout", {
          booking_ids: ids, cgv_accepted: true, cgv_version: LEGAL_VERSION,
          origin_url: window.location.origin,
        });
        onOpenChange(false);
        redirectToCheckout(ck.url, () => nav("/dashboard"));
      } else {
        toast.success(`${data.created_count} cours réservé(s) !`);
        onOpenChange(false);
        nav("/dashboard");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Réserver une série de cours récurrents</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Établissement</Label>
            <select className="w-full border rounded-lg p-2 mt-1" value={form.pool_id} onChange={(e) => setForm({ ...form, pool_id: e.target.value })} data-testid="recur-pool">
              <option value="">Choisir...</option>
              {attachedPoolIds.map((pid) => <option key={pid} value={pid}>{pools[pid]?.name || pid}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Jour de la semaine</Label>
              <select className="w-full border rounded-lg p-2 mt-1" value={form.weekday} onChange={(e) => setForm({ ...form, weekday: parseInt(e.target.value) })} data-testid="recur-weekday">
                {WD.map((w, i) => <option key={w} value={i}>{w}</option>)}
              </select>
            </div>
            <div>
              <Label>Nombre de semaines</Label>
              <input type="number" min="1" max="26" className="w-full border rounded-lg p-2 mt-1" value={form.weeks} onChange={(e) => setForm({ ...form, weeks: parseInt(e.target.value) })} data-testid="recur-weeks" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Heure</Label>
              <input type="number" min="0" max="23" className="w-full border rounded-lg p-2 mt-1" value={form.hour} onChange={(e) => setForm({ ...form, hour: parseInt(e.target.value) })} data-testid="recur-hour" />
            </div>
            <div>
              <Label>Minutes</Label>
              <input type="number" min="0" max="59" step="15" className="w-full border rounded-lg p-2 mt-1" value={form.minute} onChange={(e) => setForm({ ...form, minute: parseInt(e.target.value) })} />
            </div>
            <div>
              <Label>Durée (min)</Label>
              <input type="number" min="30" max="180" step="15" className="w-full border rounded-lg p-2 mt-1" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) })} />
            </div>
          </div>
          <div>
            <Label>Mode de paiement</Label>
            <RadioGroup value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })} className="grid grid-cols-2 gap-2 mt-1">
              <label className={`border rounded-xl p-2 cursor-pointer flex items-center gap-2 text-sm ${form.payment_method === "online" ? "border-sky-600 bg-sky-50" : ""}`}>
                <RadioGroupItem value="online" data-testid="recur-pay-online" /><span>En ligne (toutes les séances en 1 fois)</span>
              </label>
              <label className={`border rounded-xl p-2 cursor-pointer flex items-center gap-2 text-sm ${form.payment_method === "cash" ? "border-sky-600 bg-sky-50" : ""}`}>
                <RadioGroupItem value="cash" data-testid="recur-pay-cash" /><span>Espèces (séance par séance)</span>
              </label>
            </RadioGroup>
          </div>
          {form.payment_method === "online" && (
            <label className="flex items-start gap-2 bg-sky-50 border-2 border-sky-200 rounded-xl p-2 cursor-pointer text-xs">
              <input type="checkbox" checked={cgvAccepted} onChange={(e) => setCgvAccepted(e.target.checked)} className="mt-0.5" data-testid="recur-cgv" />
              <span>Je reconnais avoir lu et accepté les <Link to="/legal/cgv" target="_blank" className="text-sky-700 underline">CGV</Link> et les <Link to="/legal/cgu" target="_blank" className="text-sky-700 underline">CGU</Link>.</span>
            </label>
          )}
          {check && (
            <div className={`p-3 rounded-xl text-sm ${check.available_count > 0 ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="recur-check">
              <div className="font-semibold">Aperçu : {check.available_count} cours disponibles sur {check.results.length}</div>
              <div className="mt-1 grid grid-cols-2 gap-1 text-xs">
                {check.results.slice(0, 8).map((r) => (
                  <div key={r.date} className="flex items-center gap-1">
                    {r.available ? <span className="text-emerald-600">✓</span> : <span className="text-amber-600">✗</span>}
                    <span>{new Date(r.date).toLocaleDateString("fr-FR")}</span>
                  </div>
                ))}
              </div>
              {check.available_count === 0 && <div className="text-xs mt-2">Le MNS n'a pas publié de créneau correspondant. Demandez-lui via la messagerie ou choisissez un autre horaire.</div>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !check || check.available_count === 0} className="rounded-full bg-sky-700 hover:bg-sky-600 w-full" data-testid="recur-submit">
            {busy ? "Réservation..." : "Réserver la série"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
